import React from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AddIcon, ArrowDownIcon } from '@components/icons/svg-icon.tsx';
import ContentTypeChip from '@modules/digital-signage/components/group-settings/components/default-screen-or-playlist/components/default-content-select/components/content-type-chip/content-type-chip.tsx';
import {
  AssignedEntityEnum,
  DefaultContentSelectProps
} from '@modules/digital-signage/model/group-settings.model.ts';
import {
  CONTENT_GROUP_LABELS,
  CONTENT_TYPE_CHIP_CLASSES,
  CONTENT_TYPE_LABELS,
  filterDefaultContentOptions,
  isSameDefaultContent
} from '@modules/digital-signage/components/group-settings/utils/group-settings.utils.ts';

const DefaultContentSelect: React.FC<DefaultContentSelectProps> = ({
  value,
  options,
  loading,
  onChange,
  onCreate
}) => {
  const { t } = useTranslation();

  return (
    <FormControl className="w-full">
      <FormLabel>{t('label_default_content')}</FormLabel>
      <Autocomplete
        options={options}
        value={value}
        popupIcon={<ArrowDownIcon className="w-4.5!" />}
        noOptionsText={t('no_results_found')}
        clearText={t('clear_selection')}
        getOptionLabel={(option) => option.title}
        getOptionKey={(option) =>
          option.isCreate ? 'create' : `${option.type}-${option.id}`
        }
        isOptionEqualToValue={isSameDefaultContent}
        groupBy={(option) =>
          option.isCreate ? '' : t(CONTENT_GROUP_LABELS[option.type])
        }
        filterOptions={filterDefaultContentOptions}
        onChange={(_, option) => {
          if (!option?.isCreate) onChange(option);
        }}
        renderGroup={({ key, group, children }) => (
          <li key={key}>
            {group && (
              <Typography
                variant="caption"
                component="div"
                className="px-2! pt-2! pb-1! text-(--gray-2)!"
              >
                {group}
              </Typography>
            )}
            <ul className="p-0">{children}</ul>
          </li>
        )}
        renderOption={({ key, ...props }, option) =>
          option.isCreate ? (
            <li
              {...props}
              key={key}
              className={`${props.className ?? ''} flex-col! items-stretch! gap-2 mt-1 border-t border-(--gray-9) cursor-default! hover:bg-transparent!`}
            >
              <Typography
                variant="caption"
                className="text-(--gray-2)! truncate"
              >
                {t('digital_signage_create_new', { name: option.title })}
              </Typography>
              <div className="grid grid-cols-2 gap-2">
                {Object.values(AssignedEntityEnum).map((type) => (
                  <Button
                    key={type}
                    variant="text"
                    size="small"
                    startIcon={<AddIcon className="w-4! h-4!" />}
                    className={`w-full! min-w-0! h-8! rounded-lg! hover:opacity-80 ${CONTENT_TYPE_CHIP_CLASSES[type]}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      onCreate(option.title, type);
                    }}
                  >
                    {t(CONTENT_TYPE_LABELS[type])}
                  </Button>
                ))}
              </div>
            </li>
          ) : (
            <li
              {...props}
              key={key}
              className={`${props.className ?? ''} flex! items-center gap-2 pe-10!`}
            >
              <Typography variant="body2" className="min-w-0 truncate">
                {option.title}
              </Typography>
              <ContentTypeChip type={option.type} />
            </li>
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('placeholder_default_content')}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                className: `${params.inputProps.className ?? ''} ${value && !loading ? 'grow-0! w-auto! max-w-3/5! pe-1! [field-sizing:content]' : ''}`
              },
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && (
                      <InputAdornment position="start">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    )}
                    {value && !loading && (
                      <InputAdornment position="end" className="m-0!">
                        <ContentTypeChip type={value.type} />
                      </InputAdornment>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
    </FormControl>
  );
};

export default DefaultContentSelect;
